import { useState } from 'react';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { resetBlackFridayState } from '../../../lib/black-friday-storage';
import { setBlackFridaySim, useBlackFridaySim, type BlackFridaySim } from '../../../lib/black-friday-sim';
import { BLACK_FRIDAY_PROMO_CODE } from '../../../lib/black-friday';
import { redeemPromo } from '../../../lib/redeem-promo';
import { CREDITS_REFRESH_EVENT } from '../../../lib/use-credits';
import {
  BLACK_FRIDAY_TEST_CODES,
  blackFridayPromoCode,
  setBlackFridayTestCode,
  useBlackFridayTestCode,
  type BlackFridayTestCode,
} from '../../../lib/black-friday-dev-code';

// Dev-only simulator for the Black Friday campaign's 48-hour clock. Picking a point here substitutes the
// free-usage WINDOW (see `use-free-usage.ts`), the same trick the credits-widget sim plays on the credit
// balance — so the band, the drawer's plan card, the Settings plan card and the "Free until …" line all
// repaint together and LIVE. Inert without a dev-tools grant.
//
// The campaign spec is the other half of the gate and is NOT forced here: turn it on from
// Dev Tools ▸ Feature toggles (`specs.symphony.BlackFridayCampaign`). Two switches rather than one,
// deliberately — the spec decides whether the campaign exists for this account at all, and that is the
// thing you most want to be able to check on its own.

const OPTIONS: { value: BlackFridaySim; label: string; hint: string }[] = [
  { value: 'offer', label: 'Offer', hint: 'Before redeeming — the band offers the 48 free hours, and the modal opens once' },
  { value: 'running', label: 'Counting down', hint: '24h left — “Free unlimited credits”, and the plan card is titled for the campaign' },
  { value: 'final', label: 'Final hours', hint: '3h left — “Final hours of unlimited credits” (the copy switches under 6h)' },
  { value: 'ended-plans', label: 'Over · no wins', hint: 'The window has closed and nothing landed in it — “See plans”, on any plan' },
  { value: 'ended-wins', label: 'Over · with wins', hint: 'A win landed inside the window — “See your wins”' },
];

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevBlackFridayPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <BlackFridayPanel {...props} />;
}

const CODE_OPTIONS: { value: BlackFridayTestCode | null; label: string }[] = [
  { value: null, label: `Real · ${BLACK_FRIDAY_PROMO_CODE}` },
  ...BLACK_FRIDAY_TEST_CODES.map((code) => ({ value: code, label: `Test · ${code}` })),
];

function BlackFridayPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sim = useBlackFridaySim();
  const select = (value: BlackFridaySim) => setBlackFridaySim(sim === value ? null : value);
  const testCode = useBlackFridayTestCode();
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A real redemption is only visible with the sim off (the sim replaces the window it would show),
  // and the offer only stands on a browser that remembers no dismissal and no seen modal.
  const pickCode = (value: BlackFridayTestCode | null) => {
    setBlackFridayTestCode(value);
    setBlackFridaySim(null);
    resetBlackFridayState();
    setOutcome(null);
  };
  // The band's CTA exists only in the OFFER state, and an account that ever had a free-hours window is
  // in an OVER state for good (`lastFreeUsageUntil` never clears). This button is how such an account
  // still reaches the countdown. Raw outcomes, not copy: this is developer chrome.
  const redeemNow = () => {
    setBusy(true);
    void redeemPromo(blackFridayPromoCode(), 'black_friday_dev_tools').then((result) => {
      setBusy(false);
      setOutcome(result.outcome);
      if (result.outcome === 'redeemed' || result.outcome === 'already') window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
    });
  };

  return (
    <Modal open={open} onClose={onClose} width={520} maxHeightPx={650} scrollBody title="Black Friday" footer={<Button onClick={onClose}>Done</Button>}>
      <Typography variant="p3" color="secondary">
        Put THIS browser at a point on the campaign&apos;s 48-hour clock. It substitutes the free-usage
        window, so the band, both plan cards and the “Free until …” line move together. The campaign spec
        is the other half of the gate — turn <code>specs.symphony.BlackFridayCampaign</code> on from
        Feature toggles. Inert without a dev-tools grant.
      </Typography>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        <Button fullWidth variant={sim === null ? 'contained' : 'outlined'} onClick={() => setBlackFridaySim(null)}>
          Off · use the real account
        </Button>
        {OPTIONS.map((opt) => (
          <Button key={opt.value} fullWidth variant={sim === opt.value ? 'contained' : 'outlined'} onClick={() => select(opt.value)}>
            {opt.label}
          </Button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
        {OPTIONS.map((opt) => (
          <Typography key={opt.value} variant="p3" color="secondary">
            <strong>{opt.label}</strong> — {opt.hint}
          </Typography>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        <Typography variant="p3" color="secondary">
          <strong>Code the offer redeems.</strong> A test code is a REAL redemption on this account, so the
          countdown shows on every device. It cannot be undone from here: switching back only changes the
          code the next press spends. Each code works once per account — use &quot;New version&quot; in
          Symphony Live ▸ Promo codes to redeem it again.
        </Typography>
        {CODE_OPTIONS.map((opt) => (
          <Button
            key={opt.label}
            fullWidth
            variant={testCode === opt.value ? 'contained' : 'outlined'}
            onClick={() => pickCode(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
        <Button fullWidth color="accent" loading={busy} onClick={redeemNow}>
          Redeem {blackFridayPromoCode()} now
        </Button>
        {outcome && (
          <Typography variant="p3" color="secondary">
            Outcome: <code>{outcome}</code>
          </Typography>
        )}
      </div>

      {/* The dismissals and the "modal seen" latch are persisted, so without this the offer band and the
          modal can each be seen exactly once per browser while building them. */}
      <div style={{ marginTop: 16 }}>
        <Button fullWidth variant="outlined" onClick={resetBlackFridayState}>
          Forget the dismissals and the seen modal
        </Button>
      </div>
    </Modal>
  );
}
